import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, TrendingUp, Eye, Check, AlertTriangle, RefreshCw,
  PoundSterling, Loader2, Sparkles, Inbox,
} from "lucide-react";
import { BrandLink } from "@/components/Brand";
import { AiActivityCard } from "@/components/AiActivityCard";

const PERIODS = [
  { d: 7, label: "7 days" },
  { d: 30, label: "30 days" },
  { d: 90, label: "90 days" },
  { d: 365, label: "12 months" },
  { d: 0, label: "All time" },
];

const URGENCY_COLOUR = {
  red: { dot: "bg-destructive", text: "text-destructive", bg: "bg-destructive/10", border: "border-destructive/30" },
  amber: { dot: "bg-warning", text: "text-warning", bg: "bg-warning/10", border: "border-warning/30" },
  green: { dot: "bg-success", text: "text-success", bg: "bg-success/10", border: "border-success/30" },
};

function StatTile({ label, value, sub, icon: Icon, accent = "primary", testid }) {
  return (
    <div className="card-default p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="label-overline">{label}</div>
        {Icon && <Icon className={`w-4 h-4 text-${accent}`} />}
      </div>
      <div className="font-display font-bold text-3xl mt-3 tracking-tight">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1.5">{sub}</div>}
    </div>
  );
}

function UrgencyBar({ label, urgency, total, approved, declined, value }) {
  const c = URGENCY_COLOUR[urgency];
  const pending = Math.max(0, total - approved - declined);
  const seg = (n) => total ? `${(n / total) * 100}%` : "0%";
  return (
    <div className="space-y-2" data-testid={`urgency-row-${urgency}`}>
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />
          <span className="font-semibold">{label}</span>
          <span className="text-xs text-muted-foreground">({total} item{total === 1 ? "" : "s"})</span>
        </div>
        <span className="font-mono-plex text-xs text-muted-foreground">£{value.toFixed(0)} quoted</span>
      </div>
      {total > 0 && (
        <div className="flex h-2 rounded-full overflow-hidden bg-muted">
          <div className="bg-success transition-all" style={{ width: seg(approved) }} title={`${approved} approved`} />
          <div className="bg-muted-foreground/30 transition-all" style={{ width: seg(pending) }} title={`${pending} pending`} />
          <div className="bg-destructive transition-all" style={{ width: seg(declined) }} title={`${declined} declined`} />
        </div>
      )}
      <div className="flex justify-between text-[11px] text-muted-foreground">
        <span><span className="text-success font-semibold">{approved}</span> approved</span>
        <span><span className="text-destructive font-semibold">{declined}</span> declined</span>
      </div>
    </div>
  );
}

function FunnelRow({ label, count, prevCount, isFirst }) {
  const rate = prevCount ? Math.round((count / prevCount) * 100) : null;
  const widthPct = prevCount ? Math.max(8, (count / prevCount) * 100) : 100;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="font-semibold">{label}</span>
        <span className="font-mono-plex">
          <span className="font-bold">{count}</span>
          {!isFirst && rate !== null && (
            <span className="text-muted-foreground ml-2">{rate}%</span>
          )}
        </span>
      </div>
      <div className="bg-muted rounded h-7 relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-primary/80 rounded transition-all"
          style={{ width: `${widthPct}%` }}
        />
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    api.get(`/analytics?days=${days}`)
      .then(r => setData(r.data))
      .catch(() => toast.error("Couldn't load analytics"))
      .finally(() => setLoading(false));
  }, [days]);
  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-background" data-testid="analytics-page">
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="btn-outline" data-testid="analytics-back-btn">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <BrandLink />
          </div>
          <button onClick={refresh} className="btn-outline" data-testid="analytics-refresh-btn" title="Refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-6 py-10 space-y-8">
        {/* Title + period */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="label-overline">Conversion analytics</div>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter mt-2">
              Where the <span className="text-primary">money</span> shows up.
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Quote → view → approve → recover. Use these numbers when pitching ServiceSell PRO to your dealership management.
            </p>
          </div>
          <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/40 border border-border" data-testid="analytics-period-tabs">
            {PERIODS.map(p => (
              <button
                key={p.d}
                onClick={() => setDays(p.d)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${days === p.d ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`analytics-period-${p.d}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {loading || !data ? (
          <div className="py-20 grid place-items-center text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : data.summary.total_jobs === 0 ? (
          <div className="card-default p-12 text-center" data-testid="analytics-empty">
            <Inbox className="w-10 h-10 mx-auto text-muted-foreground" />
            <div className="font-display font-bold text-xl mt-4">No jobs in this period yet.</div>
            <p className="text-sm text-muted-foreground mt-1">Come back once you've sent a few quotes — your numbers will tell a story.</p>
          </div>
        ) : (
          <>
            {/* Headline tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatTile
                label="Quoted"
                value={`£${data.revenue.quoted.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`}
                sub={`Across ${data.summary.sent} quote${data.summary.sent === 1 ? "" : "s"}`}
                icon={PoundSterling}
                accent="primary"
                testid="stat-quoted"
              />
              <StatTile
                label="Approved"
                value={`£${data.revenue.approved.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`}
                sub={`${data.summary.approval_rate}% approval rate`}
                icon={Check}
                accent="success"
                testid="stat-approved"
              />
              <StatTile
                label="Recovered"
                value={`£${data.revenue.recovered.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`}
                sub={`${data.reconsider.red_reconsidered} of ${data.reconsider.red_declined} red declines won back`}
                icon={Sparkles}
                accent="primary"
                testid="stat-recovered"
              />
              <StatTile
                label="Reconsider rate"
                value={`${data.reconsider.reconsider_rate}%`}
                sub={`Red items reconsidered after follow-up`}
                icon={TrendingUp}
                accent="primary"
                testid="stat-reconsider-rate"
              />
            </div>

            {/* Funnel */}
            <div className="grid md:grid-cols-12 gap-6">
              <div className="md:col-span-7 card-default p-6">
                <div className="label-overline">The funnel</div>
                <h2 className="font-display font-bold text-2xl mt-1">Quote → view → approve.</h2>
                <p className="text-sm text-muted-foreground mt-1">Each step shows the conversion from the previous.</p>
                <div className="mt-6 space-y-4" data-testid="analytics-funnel">
                  <FunnelRow label="Quotes sent" count={data.summary.sent} prevCount={data.summary.sent} isFirst />
                  <FunnelRow label="Customer viewed" count={data.summary.viewed} prevCount={data.summary.sent} />
                  <FunnelRow label="Customer responded" count={data.summary.responded} prevCount={data.summary.viewed || data.summary.sent} />
                  <FunnelRow label="Won (some/all approved)" count={data.summary.won} prevCount={data.summary.responded || 1} />
                </div>
              </div>

              {/* Pitch card */}
              <div className="md:col-span-5 card-default p-6 bg-gradient-to-br from-primary/10 via-card to-card border-primary/40 flex flex-col justify-between" data-testid="analytics-pitch">
                <div>
                  <div className="label-overline text-primary">The story</div>
                  <h2 className="font-display font-bold text-2xl mt-2 leading-tight">
                    {data.summary.sent > 0 ? (
                      <>
                        Of <span className="text-primary">{data.reconsider.red_declined}</span> urgent items declined,
                        the follow-up brought
                        <span className="text-primary"> {data.reconsider.red_reconsidered}</span> back.
                      </>
                    ) : "Send your first quote and watch the numbers move."}
                  </h2>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  <div>
                    <div className="label-overline">Recovered</div>
                    <div className="font-display font-bold text-2xl text-primary">£{data.revenue.recovered.toLocaleString("en-GB", { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div>
                    <div className="label-overline">Avg quote</div>
                    <div className="font-display font-bold text-2xl">£{data.revenue.average_quote.toLocaleString("en-GB", { maximumFractionDigits: 0 })}</div>
                  </div>
                </div>
                <Link to="/how-it-works" className="btn-primary text-sm mt-6 w-fit" data-testid="analytics-pitch-deck-btn">
                  Open pitch deck <Sparkles className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Urgency breakdown */}
            <div className="card-default p-6">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="label-overline">Traffic-light performance</div>
                  <h2 className="font-display font-bold text-2xl mt-1">Approval &amp; decline by urgency</h2>
                </div>
                {data.summary.follow_ups_pending > 0 && (
                  <div className="flex items-center gap-2 text-sm bg-destructive/10 text-destructive border border-destructive/30 rounded-md px-3 py-1.5" data-testid="analytics-followups-alert">
                    <AlertTriangle className="w-4 h-4" />
                    <span><span className="font-bold">{data.summary.follow_ups_pending}</span> red follow-up{data.summary.follow_ups_pending === 1 ? "" : "s"} waiting</span>
                  </div>
                )}
              </div>
              <div className="mt-6 grid md:grid-cols-3 gap-6">
                <UrgencyBar label="Red — urgent" urgency="red" {...{
                  total: data.by_urgency.red.total,
                  approved: data.by_urgency.red.approved,
                  declined: data.by_urgency.red.declined,
                  value: data.by_urgency.red.value_quoted,
                }} />
                <UrgencyBar label="Amber — advisory" urgency="amber" {...{
                  total: data.by_urgency.amber.total,
                  approved: data.by_urgency.amber.approved,
                  declined: data.by_urgency.amber.declined,
                  value: data.by_urgency.amber.value_quoted,
                }} />
                <UrgencyBar label="Green — healthy" urgency="green" {...{
                  total: data.by_urgency.green.total,
                  approved: data.by_urgency.green.approved,
                  declined: data.by_urgency.green.declined,
                  value: data.by_urgency.green.value_quoted,
                }} />
              </div>
            </div>

            {/* Engagement tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatTile label="Total jobs" value={data.summary.total_jobs} icon={Inbox} testid="stat-total-jobs" />
              <StatTile label="View rate" value={`${data.summary.view_rate}%`} sub="Sent → viewed" icon={Eye} testid="stat-view-rate" />
              <StatTile label="Response rate" value={`${data.summary.response_rate}%`} sub="Viewed → replied" icon={Check} testid="stat-response-rate" />
              <StatTile label="Avg approved" value={`£${data.revenue.average_approved.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`} sub="Per responding customer" icon={PoundSterling} testid="stat-avg-approved" />
            </div>
          </>
        )}
        <AiActivityCard days={days} />
      </main>
    </div>
  );
}
